import React from "react";
import PropTypes from "prop-types";
import {
    Form as FormikForm,
    Masks,
    DateTimePickerField,
    DatePickerField,
    AutocompleteField,
    InputField,
    TextareaField,
} from "components/common_components/Formik";
import * as Yup from "yup";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import FormGroup from "@material-ui/core/FormGroup";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import EditIcon from "@material-ui/icons/Edit";
import BackspaceIcon from "@material-ui/icons/ArrowBack";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
    formControl: {
        margin: "unset",
        paddingTop: theme.spacing(3),
    },
    buttons: {
        marginTop: theme.spacing(2),
    },
    button: {
        margin: theme.spacing(0, 1, 0, 0),
    },
}));

function Form({ visitor, resources, mode, onEdit, ...rest }) {
    const classes = useStyles();
    const readOnly = mode === "detail";
    const { countries } = resources;

    const validationSchema = Yup.object({
        firstName: Yup.string().required("Required field"),
        lastName: Yup.string().required("Required field"),
        homePhone: Yup.string()
            .test("Valid home phone", "Incorrect format. Example 123-456-7890", (value) =>
                value ? /^\d\d\d-\d\d\d-\d\d\d\d/.test(value) : true,
            )
            .required("Required field"),
        visitDate: Yup.date().required("Required field"),
        dob: Yup.date().required("Required field"),
        ssn: Yup.string()
            .test("Valid SSN", "Incorrect format. Example 123-45-6789", (value) =>
                value ? /^\d\d\d-\d\d-\d\d\d\d/.test(value) : true,
            )
            .notRequired(),
        workPhone: Yup.string()
            .test("Valid work phone", "Incorrect format. Example 123-45-6789", (value) =>
                value ? /^\d\d\d-\d\d\d-\d\d\d\d/.test(value) : true,
            )
            .notRequired(),
    });

    return (
        <div className="layout">
            <FormikForm
                validateOnChange={false}
                initialValues={visitor}
                validationSchema={validationSchema}
                showButtons={!readOnly}
                {...rest}
            >
                <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                        <FormControl component="fieldset" className={classes.formControl}>
                            <FormLabel component="legend">General Information</FormLabel>
                            <FormGroup row>
                                <InputField disabled={readOnly} fieldName="ssn" label="SSN" lowercase mask={Masks.ssn} light compact />
                                <InputField disabled={readOnly} fieldName="firstName" label="First Name *" light compact />
                                <InputField disabled={readOnly} fieldName="middleName" label="Middle Name" light compact />
                                <InputField disabled={readOnly} fieldName="lastName" label="Last Name *" light compact />
                                <DatePickerField disabled={readOnly} fieldName="dob" label="Date of Birth *" />
                            </FormGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl component="fieldset" className={classes.formControl}>
                            <FormLabel component="legend">Location</FormLabel>
                            <FormGroup row>
                                <AutocompleteField
                                    disabled={readOnly}
                                    fieldName="country"
                                    label="Country"
                                    options={countries}
                                    valueKey="code"
                                    labelKey="name"
                                />
                                <InputField disabled={readOnly} fieldName="state" label="State" light compact />
                                <InputField disabled={readOnly} fieldName="city" label="City" light compact />
                                <InputField disabled={readOnly} fieldName="zipCode" label="Zip Code" light compact />
                                <InputField disabled={readOnly} fieldName="address1" label="Address 1" light compact />
                                <InputField disabled={readOnly} fieldName="address2" label="Address 2" light compact />
                            </FormGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <FormControl component="fieldset" className={classes.formControl}>
                            <FormLabel component="legend">Contact</FormLabel>
                            <FormGroup row>
                                <InputField
                                    disabled={readOnly}
                                    fieldName="homePhone"
                                    label="Home Phone *"
                                    mask={Masks.phoneNumber}
                                    light
                                    compact
                                />
                                <InputField
                                    disabled={readOnly}
                                    fieldName="workPhone"
                                    label="Work Phone"
                                    mask={Masks.phoneNumber}
                                    light
                                    compact
                                />
                            </FormGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl component="fieldset" className={classes.formControl}>
                            <FormLabel component="legend">Visit</FormLabel>
                            <FormGroup row>
                                <DateTimePickerField disabled={readOnly} fieldName="visitDate" label="Visit Date *" />
                                <TextareaField disabled={readOnly} fieldName="notes" label="Notes" fullWidth maxLength={500} rows={10} />
                            </FormGroup>
                        </FormControl>
                    </Grid>
                </Grid>
                {readOnly && (
                    <Grid container spacing={1} className={classes.buttons}>
                        <Grid item xs={12} sm={6}>
                            <Button
                                variant="outlined"
                                size="small"
                                color="default"
                                startIcon={<BackspaceIcon />}
                                className={classes.button}
                                onClick={rest.onCancel}
                            >
                                Back
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                color="primary"
                                startIcon={<EditIcon />}
                                className={classes.button}
                                onClick={onEdit}
                            >
                                Edit
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </FormikForm>
        </div>
    );
}

Form.propTypes = {
    visitor: PropTypes.shape({
        visitDate: PropTypes.instanceOf(Date),
        ssn: PropTypes.string,
        firstName: PropTypes.string,
        middleName: PropTypes.string,
        lastName: PropTypes.string,
        address1: PropTypes.string,
        address2: PropTypes.string,
        country: PropTypes.shape({
            code: PropTypes.string,
            name: PropTypes.string,
        }),
        city: PropTypes.string,
        state: PropTypes.string,
        zipCode: PropTypes.string,
        dob: PropTypes.instanceOf(Date),
        homePhone: PropTypes.string,
        workPhone: PropTypes.string,
        notes: PropTypes.string,
    }),
    resources: PropTypes.shape({
        countries: PropTypes.arrayOf(
            PropTypes.shape({
                code: PropTypes.string,
                name: PropTypes.string,
            }),
        ),
    }),
    mode: PropTypes.oneOf(["add", "detail", "update"]).isRequired,
    onEdit: PropTypes.func,
};

Form.defaultProps = {
    visitor: {
        visitDate: new Date(),
        ssn: "",
        firstName: "",
        middleName: "",
        lastName: "",
        address1: "",
        address2: "",
        country: null,
        city: "",
        state: "",
        zipCode: "",
        dob: new Date(),
        homePhone: "",
        workPhone: "",
        notes: "",
    },
    resources: {
        countries: [],
    },
    onEdit: () => { },
};

export default Form;
